import React from 'react';
import { Lock } from 'lucide-react';

export const SENSITIVE_TEXT = {
  hidden: 'مخفي لأسباب أمنية',
  unauthorized: 'ليست لديك صلاحية لعرض هذه البيانات',
  protected: 'هذه البيانات محمية',
  contactAdmin: 'تواصل مع المسؤول للحصول على الصلاحية',
  unavailable: 'غير متوفر',
  sensitive: 'حساس',
};

export function isValuePresent(value) {
  return value !== null && value !== undefined && String(value).trim() !== '';
}

export function pickSensitiveValue(record, field, { allowRaw = false } = {}) {
  if (!record || !field) return { value: null, source: 'missing' };
  const maskedKey = `${field}_masked`;
  if (isValuePresent(record[maskedKey])) {
    return { value: record[maskedKey], source: 'masked' };
  }
  if (isValuePresent(record[field])) {
    return allowRaw ? { value: record[field], source: 'raw' } : { value: null, source: 'unauthorized' };
  }
  if (Object.prototype.hasOwnProperty.call(record, maskedKey) && !isValuePresent(record[maskedKey])) {
    return { value: null, source: 'missing' };
  }
  if (!Object.prototype.hasOwnProperty.call(record, field)) {
    return { value: null, source: 'unauthorized' };
  }
  return { value: null, source: 'missing' };
}

export function formatSensitiveMoney(value, currency = 'د.إ') {
  if (!isValuePresent(value)) return null;
  if (typeof value === 'number') {
    return `${value.toLocaleString('en-AE', { minimumFractionDigits: 2 })} ${currency}`;
  }
  return String(value);
}

export function SensitiveBadge({ text = SENSITIVE_TEXT.sensitive, description = SENSITIVE_TEXT.protected, className = '' }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border border-amber-300/70 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800 dark:border-amber-700/70 dark:bg-amber-950/40 dark:text-amber-200 ${className}`}
      title={description}
      aria-label={`${text} - ${description}`}
      dir="rtl"
    >
      <Lock className="h-3 w-3" aria-hidden="true" />
      {text}
    </span>
  );
}

export function PermissionHint({ message = SENSITIVE_TEXT.unauthorized, showContact = true, className = '' }) {
  return (
    <span
      className={`inline-flex flex-col gap-0.5 text-xs text-muted-foreground ${className}`}
      title={showContact ? SENSITIVE_TEXT.contactAdmin : message}
      dir="rtl"
    >
      <span>{message}</span>
      {showContact && <span>{SENSITIVE_TEXT.contactAdmin}</span>}
    </span>
  );
}

export function MaskedValue({
  value,
  source,
  unavailableText = SENSITIVE_TEXT.unavailable,
  hiddenText = SENSITIVE_TEXT.hidden,
  description = SENSITIVE_TEXT.protected,
  showBadge = true,
  className = '',
  valueClassName = '',
}) {
  const hasValue = isValuePresent(value);
  const isUnauthorized = source === 'unauthorized';
  const displayValue = hasValue ? String(value) : (isUnauthorized ? hiddenText : unavailableText);

  return (
    <span
      className={`inline-flex items-center gap-2 align-middle ${className}`}
      title={hasValue ? description : (isUnauthorized ? SENSITIVE_TEXT.unauthorized : unavailableText)}
      dir="rtl"
    >
      <span className={`font-mono text-foreground ${!hasValue ? 'font-sans text-muted-foreground' : ''} ${valueClassName}`}>
        {displayValue}
      </span>
      {showBadge && hasValue && <SensitiveBadge />}
      {isUnauthorized && <PermissionHint className="sr-only" />}
    </span>
  );
}

export function SensitiveFieldRow({ label, value, source, description = SENSITIVE_TEXT.protected, className = '' }) {
  return (
    <div className={`flex items-start justify-between gap-3 text-sm ${className}`} dir="rtl">
      <span className="text-muted-foreground">{label}</span>
      <MaskedValue value={value} source={source} description={description} />
    </div>
  );
}
